import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import {
  searchAjaxPageQueryOptions,
  searchPageQueryOptions,
  type SearchPageQueryInput,
} from "~/app/api/pageData/searchPageQuery";
import type { SearchAjaxPageInitialData, SearchPageInitialData } from "~/app/api/pageData/searchPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createSearchPage } from "~/pages/SearchPage/SearchPage";

/**
 * Search route family.
 *
 * The legacy Spring {@code SearchPageController} exposes four routes under
 * {@code /search}: text-search ({@code params = "!q"}), refine-search
 * ({@code params = "q"}), ajax ({@code /search/searchpage}), and designation
 * redirect ({@code /search/searchpage/designation}). After the body-only
 * migration all render routes return {@code GenericReactPage.Page}, so the SPA
 * must match two distinct paths:
 *
 * - {@code /search} — covers both the {@code !q} and {@code q} Spring variants.
 *   TanStack Router does not distinguish by the {@code q} search param, and the
 *   propless {@code SearchPage} loads its own results via RTK Query, so one
 *   route is sufficient. The loader fetches the text-search page-data endpoint
 *   for shell metadata only.
 * - {@code /search/searchpage} — the ajax variant. Hit as a full page load by
 *   {@code SearchBox} submit, {@code SEARCH_PAGE} links, and the designation
 *   redirect target. Its loader fetches the ajax page-data endpoint.
 *
 * The designation route ({@code /search/searchpage/designation}) is redirect-
 * only: the Spring controller returns {@code redirect:} before the SPA boots,
 * so no TanStack route is registered for it. It is intentionally backend-only.
 */
function SearchRoute() {
  const result = useLoaderData({
    from: "/search",
  }) as PageInitialData<SearchPageInitialData>;
  const shellProps = useStorefrontShellProps();

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createSearchPage({ recommendationPlacement: result.data?.recommendationPlacement })}
    </StorefrontShellFrame>
  );
}

function SearchAjaxRoute() {
  const result = useLoaderData({
    from: "/search/searchpage",
  }) as PageInitialData<SearchAjaxPageInitialData>;
  const shellProps = useStorefrontShellProps();

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createSearchPage({ recommendationPlacement: result.data?.recommendationPlacement })}
    </StorefrontShellFrame>
  );
}

export function parseSearchPageQuery(search: Record<string, unknown>): SearchPageQueryInput {
  const text = stringValue(search.text, "");
  const techValues = nullableStringValue(search.techValues);
  const isSelectionAssistant = stringValue(search.isSelectionAssistant, "");

  if (typeof search.q === "string") {
    return {
      variant: "refine",
      q: search.q,
      page: normalizePage(search.page),
      show: nonEmptyStringValue(search.show, "Page"),
      sort: nullableStringValue(search.sort),
      text,
      techValues,
      assistantValues: nullableStringValue(search.assistantValues),
      isSelectionAssistant,
    };
  }

  return {
    variant: "text",
    text,
    techValues,
    assistantValues: nullableStringValue(search.asssistantValues),
    isSelectionAssistant,
  };
}

function stringValue(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}

function nullableStringValue(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function nonEmptyStringValue(value: unknown, fallback: string): string {
  return typeof value === "string" && value ? value : fallback;
}

function normalizePage(value: unknown): string {
  if (typeof value !== "string" || !value) {
    return "0";
  }
  if (!/^[+-]?\d+$/.test(value)) {
    return value;
  }
  const page = Number(value);
  if (!Number.isInteger(page) || page < -2147483648 || page > 2147483647) {
    return value;
  }
  return String(page);
}

export async function loadSearchPageData(context: { queryClient: QueryClient }, input: SearchPageQueryInput) {
  const result = await context.queryClient.ensureQueryData(searchPageQueryOptions(input));

  return unwrapPageInitialData(result);
}

export async function loadSearchAjaxPageData(context: { queryClient: QueryClient }) {
  const result = await context.queryClient.ensureQueryData(searchAjaxPageQueryOptions());

  return unwrapPageInitialData(result);
}

export const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/search",
  validateSearch: parseSearchPageQuery,
  // Search params drive the result request inside SearchPage. Keeping them out
  // of loaderDeps preserves the already-loaded shell page data while users
  // paginate, sort, or filter within the same SRP route.
  loader: async ({ context, location }) => loadSearchPageData(context, parseSearchPageQuery(location.search)),
  component: SearchRoute,
});

export const searchAjaxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/search/searchpage",
  loader: async ({ context }) => loadSearchAjaxPageData(context),
  component: SearchAjaxRoute,
});
